// ABOUTME: Normalizes WhatsApp pre-registration settings, options, and link exports.
// ABOUTME: Keeps CAS requests and unavailable configured references explicit in the UI.
import type { WhatsappPreRegistrationLabels } from "./whatsappPreRegistrationLabels";

export const WHATSAPP_PRE_REGISTRATION_MAX_REGIONS = 500;

export type WhatsappPreRegistrationGeneralNumberEntryMode =
    "DISABLED" | "UNMATCHED_ONLY" | "ALL_IDLE_CONTACTS";

export type WhatsappPreRegistrationRoutingMode =
    | "COMPANY"
    | "BRANCH"
    | "REGION_BRANCH";

const WHATSAPP_PRE_REGISTRATION_GENERAL_NUMBER_ENTRY_MODES = [
    "DISABLED",
    "UNMATCHED_ONLY",
    "ALL_IDLE_CONTACTS"
] as const;

const WHATSAPP_PRE_REGISTRATION_ROUTING_MODES = [
    "COMPANY",
    "BRANCH",
    "REGION_BRANCH"
] as const;

export interface WhatsappPreRegistrationRegion {
    id: string;
    name: string;
    active: boolean;
    storeIds: string[];
}

export interface WhatsappPreRegistrationUserAssignment {
    userEmail: string;
    regionIds: string[];
    storeIds: string[];
}

export interface WhatsappPreRegistrationConfig {
    enabled: boolean;
    routingMode: WhatsappPreRegistrationRoutingMode;
    generalNumberEntryMode: WhatsappPreRegistrationGeneralNumberEntryMode;
    phoneNumberId: string;
    displayPhoneNumber: string;
    flowModelId: string;
    flowModelVersion: number;
    consent: { version: string; text: string };
    futureJobs: { text: string; url: string };
    nonRecruiting: { text: string; url: string };
    branchIds: string[];
    companyUserEmails: string[];
    regions: WhatsappPreRegistrationRegion[];
    userAssignments: WhatsappPreRegistrationUserAssignment[];
}

export interface WhatsappPreRegistrationOptions {
    stores: Array<{ id: string; name: string; available?: boolean }>;
    users: Array<{ email: string; name: string; available?: boolean }>;
    flowModels: Array<{
        id: string;
        name: string;
        version: number;
        available?: boolean;
    }>;
    phoneNumbers: Array<{
        id: string;
        displayPhoneNumber: string;
        available?: boolean;
    }>;
}

export interface WhatsappPreRegistrationLinkRow {
    storeId: string;
    storeName: string;
    regionId: string;
    status: string;
    usable: boolean;
    waMeUrl: string;
}

export interface WhatsappPreRegistrationGeneralEntry {
    usable: boolean;
    waMeUrl: string;
}

export const applyWhatsappPreRegistrationRolloutChange = async ({
    enabled,
    requestChange,
    setFeatureActive,
    reloadCompanyAndPermissionData
}: {
    enabled: boolean;
    requestChange: () => Promise<void>;
    setFeatureActive: (enabled: boolean) => void;
    reloadCompanyAndPermissionData: () => Promise<void>;
}) => {
    await requestChange();
    setFeatureActive(enabled);
    await reloadCompanyAndPermissionData();
};

export const requestWhatsappPreRegistrationRolloutChange = async ({
    enabled,
    confirmChange,
    updateFeatureActive,
    reloadSettings
}: {
    enabled: boolean;
    confirmChange: () => boolean;
    updateFeatureActive: (enabled: boolean) => Promise<void>;
    reloadSettings: () => Promise<void>;
}) => {
    if (!confirmChange()) {
        return false;
    }
    await updateFeatureActive(enabled);
    await reloadSettings();
    return true;
};

const normalizeStrings = (values: unknown) =>
    (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean);

const normalizeGeneralNumberEntryMode = (
    value: unknown
): WhatsappPreRegistrationGeneralNumberEntryMode => {
    const mode = String(value || "").trim();
    return WHATSAPP_PRE_REGISTRATION_GENERAL_NUMBER_ENTRY_MODES.includes(
        mode as WhatsappPreRegistrationGeneralNumberEntryMode
    )
        ? (mode as WhatsappPreRegistrationGeneralNumberEntryMode)
        : "UNMATCHED_ONLY";
};

const normalizeRoutingMode = (
    value: unknown
): WhatsappPreRegistrationRoutingMode => {
    const mode = String(value || "").trim();
    return WHATSAPP_PRE_REGISTRATION_ROUTING_MODES.includes(
        mode as WhatsappPreRegistrationRoutingMode
    )
        ? (mode as WhatsappPreRegistrationRoutingMode)
        : "REGION_BRANCH";
};

const getRegionStoreIds = (regions: WhatsappPreRegistrationRegion[]) =>
    regions.reduce<string[]>(
        (storeIds, region) => [...storeIds, ...region.storeIds],
        []
    );

const normalizeConfig = (value: any): WhatsappPreRegistrationConfig => {
    return {
        enabled: value?.enabled === true,
        routingMode: normalizeRoutingMode(value?.routingMode),
        generalNumberEntryMode: normalizeGeneralNumberEntryMode(
            value?.generalNumberEntryMode
        ),
        phoneNumberId: String(value?.phoneNumberId || "").trim(),
        displayPhoneNumber: String(value?.displayPhoneNumber || "").trim(),
        flowModelId: String(value?.flowModelId || "").trim(),
        flowModelVersion: Number(value?.flowModelVersion || 0),
        consent: {
            version: String(value?.consent?.version || "").trim(),
            text: String(value?.consent?.text || "").trim()
        },
        futureJobs: {
            text: String(value?.futureJobs?.text || "").trim(),
            url: String(value?.futureJobs?.url || "").trim()
        },
        nonRecruiting: {
            text: String(value?.nonRecruiting?.text || "").trim(),
            url: String(value?.nonRecruiting?.url || "").trim()
        },
        branchIds: normalizeStrings(value?.branchIds).map((branchId) =>
            branchId.toLowerCase()
        ),
        companyUserEmails: normalizeStrings(value?.companyUserEmails).map(
            (email) => email.toLowerCase()
        ),
        regions: (Array.isArray(value?.regions) ? value.regions : []).map(
            (region: any) => ({
                id: String(region?.id || "").trim(),
                name: String(region?.name || "").trim(),
                active: region?.active !== false,
                storeIds: normalizeStrings(region?.storeIds)
            })
        ),
        userAssignments: (Array.isArray(value?.userAssignments)
            ? value.userAssignments
            : []
        ).map((assignment: any) => ({
            userEmail: String(assignment?.userEmail || "")
                .trim()
                .toLowerCase(),
            regionIds: normalizeStrings(assignment?.regionIds),
            storeIds: normalizeStrings(assignment?.storeIds)
        }))
    };
};

export const isWhatsappPreRegistrationRegionIdentifier = (value: unknown) =>
    /^[A-Za-z0-9_-]{1,100}$/.test(String(value || "").trim());

export const getWhatsappPreRegistrationConfigValidationError = (
    config: WhatsappPreRegistrationConfig
) => {
    if (config.regions.length > WHATSAPP_PRE_REGISTRATION_MAX_REGIONS) {
        return "whatsappPreRegistration_regionLimitExceeded";
    }
    if (
        config.enabled &&
        config.routingMode === "COMPANY" &&
        config.companyUserEmails.length === 0
    ) {
        return "whatsappPreRegistration_companyRespondersRequired";
    }
    if (
        config.enabled &&
        config.routingMode === "BRANCH" &&
        config.branchIds.length === 0
    ) {
        return "whatsappPreRegistration_branchesRequired";
    }
    if (config.routingMode !== "REGION_BRANCH") {
        return "";
    }
    const incompleteRegion = config.regions.find(
        (region) => !region.id || !region.name
    );
    if (incompleteRegion) {
        return "whatsappPreRegistration_regionInvalid";
    }

    if (
        config.regions.some(
            (region) => !isWhatsappPreRegistrationRegionIdentifier(region.id)
        )
    ) {
        return "whatsappPreRegistration_regionIdentifierInvalid";
    }

    return "";
};

export const updateWhatsappPreRegistrationRegion = (
    config: WhatsappPreRegistrationConfig,
    index: number,
    changes: Partial<WhatsappPreRegistrationRegion>
): WhatsappPreRegistrationConfig => {
    const previous = config.regions[index];
    if (!previous) {
        return config;
    }
    const regions = config.regions.map((region, regionIndex) =>
        regionIndex === index ? { ...region, ...changes } : region
    );
    const current = regions[index];
    const configuredStoreIds = new Set(getRegionStoreIds(regions));

    return {
        ...config,
        regions,
        userAssignments: config.userAssignments.map((assignment) => ({
            ...assignment,
            regionIds: assignment.regionIds
                .map((regionId) =>
                    regionId === previous.id ? current.id : regionId
                )
                .filter(Boolean),
            storeIds: assignment.storeIds.filter((storeId) =>
                configuredStoreIds.has(storeId)
            )
        }))
    };
};

export const removeWhatsappPreRegistrationRegion = (
    config: WhatsappPreRegistrationConfig,
    index: number
): WhatsappPreRegistrationConfig => {
    const removed = config.regions[index];
    if (!removed) {
        return config;
    }
    const regions = config.regions.filter(
        (_, regionIndex) => regionIndex !== index
    );
    const configuredStoreIds = new Set(getRegionStoreIds(regions));

    return {
        ...config,
        regions,
        userAssignments: config.userAssignments.map((assignment) => ({
            ...assignment,
            regionIds: assignment.regionIds.filter(
                (regionId) => regionId !== removed.id
            ),
            storeIds: assignment.storeIds.filter((storeId) =>
                configuredStoreIds.has(storeId)
            )
        }))
    };
};

const sortByLabel = <T>(values: T[], getLabel: (value: T) => string) =>
    values.sort((left, right) => getLabel(left).localeCompare(getLabel(right)));

export const canShowWhatsappPreRegistrationSettings = (
    whatsappEnabled: boolean,
    whatsappApplicationEnabled: boolean
) => whatsappEnabled && whatsappApplicationEnabled;

export const isWhatsappPreRegistrationConfigDirty = (
    draft: WhatsappPreRegistrationConfig,
    saved: WhatsappPreRegistrationConfig
) =>
    JSON.stringify(normalizeConfig(draft)) !==
    JSON.stringify(normalizeConfig(saved));

export const getWhatsappPreRegistrationAvailableFlowModelVersion = ({
    config,
    options
}: {
    config: WhatsappPreRegistrationConfig;
    options: WhatsappPreRegistrationOptions;
}): WhatsappPreRegistrationOptions["flowModels"][number] | null =>
    options.flowModels.reduce<
        WhatsappPreRegistrationOptions["flowModels"][number] | null
    >(
        (latest, model) =>
            model.id === config.flowModelId &&
            model.available !== false &&
            model.version > config.flowModelVersion &&
            (!latest || model.version > latest.version)
                ? model
                : latest,
        null
    );

export type WhatsappPreRegistrationSetupStepId =
    "integration" | "destinations" | "routing" | "owners" | "save" | "rollout";

export interface WhatsappPreRegistrationSetupStep {
    id: WhatsappPreRegistrationSetupStepId;
    complete: boolean;
}

const normalizeReference = (value: unknown) =>
    String(value || "")
        .trim()
        .toLowerCase();

export const getWhatsappPreRegistrationSetupGuide = ({
    config,
    options,
    isDirty,
    featureActive
}: {
    config: WhatsappPreRegistrationConfig;
    options: WhatsappPreRegistrationOptions;
    isDirty: boolean;
    featureActive: boolean;
}): WhatsappPreRegistrationSetupStep[] => {
    const phoneNumberReady = options.phoneNumbers.some(
        (phoneNumber) =>
            phoneNumber.id === config.phoneNumberId &&
            phoneNumber.available !== false
    );
    const flowModelReady = options.flowModels.some(
        (flowModel) =>
            flowModel.id === config.flowModelId &&
            flowModel.version === config.flowModelVersion &&
            flowModel.available !== false
    );
    const integrationComplete = Boolean(
        config.phoneNumberId &&
        phoneNumberReady &&
        config.flowModelId &&
        config.flowModelVersion > 0 &&
        flowModelReady &&
        config.consent.version &&
        config.consent.text
    );
    const destinationsComplete = Boolean(
        config.futureJobs.text &&
        config.futureJobs.url &&
        config.nonRecruiting.text &&
        config.nonRecruiting.url
    );
    const activeRegions = config.regions.filter((region) => region.active);
    const activeStoreIds = new Set(
        activeRegions.flatMap((region) =>
            region.storeIds.map(normalizeReference).filter(Boolean)
        )
    );
    const availableStoreIds = new Set(
        options.stores
            .filter((store) => store.available !== false)
            .map((store) => normalizeReference(store.id))
    );
    const branchStoreIds = new Set(
        config.branchIds.map(normalizeReference).filter(Boolean)
    );
    const routingComplete =
        config.routingMode === "COMPANY"
            ? true
            : config.routingMode === "BRANCH"
              ? Boolean(
                    branchStoreIds.size > 0 &&
                        [...branchStoreIds].every((storeId) =>
                            availableStoreIds.has(storeId)
                        )
                )
              : Boolean(
                    config.regions.length > 0 &&
                        config.regions.length <=
                            WHATSAPP_PRE_REGISTRATION_MAX_REGIONS &&
                        config.regions.every(
                            (region) => region.id.trim() && region.name.trim()
                        ) &&
                        activeStoreIds.size > 0 &&
                        [...activeStoreIds].every((storeId) =>
                            availableStoreIds.has(storeId)
                        )
                );
    const availableUserEmails = new Set(
        options.users
            .filter((user) => user.available !== false)
            .map((user) => normalizeReference(user.email))
    );
    const activeRegionIds = new Set(
        activeRegions.map((region) => normalizeReference(region.id))
    );
    const assignmentsComplete = config.userAssignments.every(
        (assignment) =>
            normalizeReference(assignment.userEmail) &&
            availableUserEmails.has(normalizeReference(assignment.userEmail)) &&
            (assignment.regionIds.length > 0 || assignment.storeIds.length > 0)
    );
    const coveredStoreIds = new Set<string>();
    for (const assignment of config.userAssignments) {
        for (const storeId of assignment.storeIds) {
            coveredStoreIds.add(normalizeReference(storeId));
        }
        for (const regionId of assignment.regionIds) {
            if (
                config.routingMode !== "REGION_BRANCH" ||
                !activeRegionIds.has(normalizeReference(regionId))
            ) {
                continue;
            }
            const region = activeRegions.find(
                (candidate) =>
                    normalizeReference(candidate.id) ===
                    normalizeReference(regionId)
            );
            for (const storeId of region?.storeIds || []) {
                coveredStoreIds.add(normalizeReference(storeId));
            }
        }
    }
    const ownersComplete =
        config.routingMode === "COMPANY"
            ? Boolean(
                  config.companyUserEmails.length > 0 &&
                      config.companyUserEmails.every((email) =>
                          availableUserEmails.has(normalizeReference(email))
                      )
              )
            : Boolean(
                  config.userAssignments.length > 0 &&
                      assignmentsComplete &&
                      [...(config.routingMode === "BRANCH"
                          ? branchStoreIds
                          : activeStoreIds)].every((storeId) =>
                          coveredStoreIds.has(storeId)
                      )
              );
    const savedConfigurationComplete = Boolean(
        config.enabled &&
        !isDirty &&
        integrationComplete &&
        destinationsComplete &&
        routingComplete &&
        ownersComplete
    );

    return [
        { id: "integration", complete: integrationComplete },
        { id: "destinations", complete: destinationsComplete },
        { id: "routing", complete: routingComplete },
        { id: "owners", complete: ownersComplete },
        {
            id: "save",
            complete: savedConfigurationComplete
        },
        {
            id: "rollout",
            complete: featureActive && savedConfigurationComplete
        }
    ];
};

export const createWhatsappPreRegistrationSettingsState = (response: any) => {
    const config = normalizeConfig(response?.config);
    const available = response?.options || {};
    const storeMap = new Map<
        string,
        WhatsappPreRegistrationOptions["stores"][number]
    >();
    for (const store of available.stores || []) {
        const id = String(store?.id || "")
            .trim()
            .toLowerCase();
        if (id) {
            storeMap.set(id, {
                id,
                name: String(store?.name || id),
                available: store?.available !== false
            });
        }
    }
    const configuredStoreIds = new Set([
        ...getRegionStoreIds(config.regions),
        ...config.branchIds,
        ...config.userAssignments.reduce<string[]>(
            (storeIds, assignment) => [...storeIds, ...assignment.storeIds],
            []
        )
    ]);
    for (const storeIdValue of configuredStoreIds) {
        const storeId = String(storeIdValue).toLowerCase();
        if (!storeMap.has(storeId)) {
            storeMap.set(storeId, {
                id: storeId,
                name: storeId,
                available: false
            });
        }
    }

    const userMap = new Map<
        string,
        WhatsappPreRegistrationOptions["users"][number]
    >();
    for (const user of available.users || []) {
        const email = String(user?.email || "")
            .trim()
            .toLowerCase();
        if (email) {
            userMap.set(email, {
                email,
                name: String(user?.name || email),
                available: user?.available !== false
            });
        }
    }
    for (const assignment of [
        ...config.userAssignments,
        ...config.companyUserEmails.map((userEmail) => ({ userEmail }))
    ]) {
        if (!userMap.has(assignment.userEmail)) {
            userMap.set(assignment.userEmail, {
                email: assignment.userEmail,
                name: assignment.userEmail,
                available: false
            });
        }
    }

    const modelMap = new Map<
        string,
        WhatsappPreRegistrationOptions["flowModels"][number]
    >();
    for (const model of available.flowModels || []) {
        const id = String(model?.id || "").trim();
        const version = Number(model?.version || 0);
        if (id && version > 0) {
            modelMap.set(`${id}:${version}`, {
                id,
                name: String(model?.name || id),
                version,
                available: model?.available !== false
            });
        }
    }
    const configuredModelKey = `${config.flowModelId}:${config.flowModelVersion}`;
    if (
        config.flowModelId &&
        config.flowModelVersion > 0 &&
        !modelMap.has(configuredModelKey)
    ) {
        modelMap.set(configuredModelKey, {
            id: config.flowModelId,
            name: config.flowModelId,
            version: config.flowModelVersion,
            available: false
        });
    }

    const phoneMap = new Map<
        string,
        WhatsappPreRegistrationOptions["phoneNumbers"][number]
    >();
    for (const phone of available.phoneNumbers || []) {
        const id = String(phone?.id || "").trim();
        if (id) {
            phoneMap.set(id, {
                id,
                displayPhoneNumber: String(phone?.displayPhoneNumber || id),
                available: phone?.available !== false
            });
        }
    }
    if (config.phoneNumberId && !phoneMap.has(config.phoneNumberId)) {
        phoneMap.set(config.phoneNumberId, {
            id: config.phoneNumberId,
            displayPhoneNumber:
                config.displayPhoneNumber || config.phoneNumberId,
            available: false
        });
    }

    return {
        config,
        generalEntry: {
            usable: response?.generalEntry?.usable === true,
            waMeUrl:
                response?.generalEntry?.usable === true
                    ? String(response?.generalEntry?.waMeUrl || "")
                    : ""
        } as WhatsappPreRegistrationGeneralEntry,
        revision:
            response?.config?.revision === undefined ||
            response?.config?.revision === null
                ? null
                : String(response.config.revision),
        options: {
            stores: sortByLabel([...storeMap.values()], (store) => store.name),
            users: sortByLabel([...userMap.values()], (user) => user.name),
            flowModels: sortByLabel(
                [...modelMap.values()],
                (model) => model.name
            ),
            phoneNumbers: sortByLabel(
                [...phoneMap.values()],
                (phone) => phone.displayPhoneNumber
            )
        } as WhatsappPreRegistrationOptions
    };
};

export const buildWhatsappPreRegistrationSaveRequest = (
    companyId: number,
    config: WhatsappPreRegistrationConfig,
    expectedRevision: string | null
) => ({ companyId, config: normalizeConfig(config), expectedRevision });

export const getWhatsappPreRegistrationLinkSharingState = (params: {
    actionsDisabled: boolean;
    featureActive: boolean;
    generalEntry: WhatsappPreRegistrationGeneralEntry;
    rows: WhatsappPreRegistrationLinkRow[];
    selectedQrUrl: string;
}) => {
    const canShare = !params.actionsDisabled && params.featureActive;
    const shareableUrls = [
        params.generalEntry.usable === true
            ? params.generalEntry.waMeUrl
            : "",
        ...params.rows
            .filter((row) => row.usable === true)
            .map((row) => row.waMeUrl)
    ].filter(Boolean);
    return {
        canShare,
        generalEntryUrl:
            canShare && params.generalEntry.usable === true
                ? params.generalEntry.waMeUrl
                : "",
        exportRows: canShare ? params.rows : [],
        selectedQrUrl:
            canShare && shareableUrls.includes(params.selectedQrUrl)
                ? params.selectedQrUrl
                : ""
    };
};

export const getWhatsappPreRegistrationSelectedQrUrl = (
    selectedQrUrl: string,
    shareableQrUrl: string
) => (selectedQrUrl === shareableQrUrl ? selectedQrUrl : "");

export const mergeWhatsappPreRegistrationLinkRows = (params: {
    config: WhatsappPreRegistrationConfig;
    stores: WhatsappPreRegistrationOptions["stores"];
    links: any[];
}): WhatsappPreRegistrationLinkRow[] => {
    if (params.config.routingMode === "COMPANY") {
        return [];
    }
    const storesById = new Map(
        params.stores.map((store) => [store.id.toLowerCase(), store])
    );
    const regionsByStoreId = new Map<string, string>();
    for (const region of params.config.regions) {
        for (const storeId of region.storeIds) {
            regionsByStoreId.set(storeId.toLowerCase(), region.id);
        }
    }
    const linksByStoreId = new Map(
        (params.links || []).map((link) => [
            String(link?.storeId || "").toLowerCase(),
            link
        ])
    );
    const configuredStoreIds =
        params.config.routingMode === "BRANCH"
            ? params.config.branchIds.map((storeId) => storeId.toLowerCase())
            : [...regionsByStoreId.keys()];
    const storeIds = new Set([
        ...configuredStoreIds,
        ...linksByStoreId.keys()
    ]);

    return [...storeIds]
        .filter(Boolean)
        .map((storeId) => {
            const link = linksByStoreId.get(storeId) || {};
            return {
                storeId,
                storeName: String(
                    link.storeName || storesById.get(storeId)?.name || storeId
                ),
                regionId: String(
                    link.regionId || regionsByStoreId.get(storeId) || ""
                ),
                status: String(link.status || "NOT_CREATED"),
                usable: link.usable === true,
                waMeUrl: String(link.waMeUrl || "")
            };
        })
        .sort(
            (left, right) =>
                left.storeName.localeCompare(right.storeName) ||
                left.storeId.localeCompare(right.storeId)
        );
};

const sanitizeCsvCell = (value: unknown) => {
    const text = String(value ?? "");
    return /^[=+\-@]/.test(text) ? `'${text}` : text;
};

const escapeCsvCell = (value: unknown) =>
    `"${sanitizeCsvCell(value).replace(/"/g, '""')}"`;

export const getWhatsappPreRegistrationLinkPresentation = ({
    row,
    regions,
    canShare,
    labels
}: {
    row: WhatsappPreRegistrationLinkRow;
    regions: WhatsappPreRegistrationRegion[];
    canShare: boolean;
    labels: Pick<
        WhatsappPreRegistrationLabels,
        "linkUnavailable" | "unknownStatus"
    >;
}) => {
    const region = regions.find((region) => region.id === row.regionId);
    const hasActiveLinkRecord = row.status.toUpperCase() === "ACTIVE";
    const hasCurrentLink =
        canShare && row.usable === true && Boolean(row.waMeUrl);

    return {
        regionName: region?.name || row.regionId || "-",
        hasCurrentLink,
        hasActiveLinkRecord,
        isActive: hasActiveLinkRecord && hasCurrentLink,
        statusLabel:
            hasActiveLinkRecord && !hasCurrentLink
                ? labels.linkUnavailable
                : row.status || labels.unknownStatus,
        shareableUrl: hasCurrentLink ? row.waMeUrl : ""
    };
};

export const buildWhatsappPreRegistrationCsv = (
    rows: WhatsappPreRegistrationLinkRow[],
    regions: WhatsappPreRegistrationRegion[],
    labels: Pick<
        WhatsappPreRegistrationLabels,
        | "store"
        | "region"
        | "status"
        | "link"
        | "linkUnavailable"
        | "unknownStatus"
    >
) =>
    [
        [labels.store, labels.region, labels.status, labels.link],
        ...rows.map((row) => {
            const presentation = getWhatsappPreRegistrationLinkPresentation({
                row,
                regions,
                canShare: true,
                labels
            });
            return [
                row.storeName,
                presentation.regionName,
                presentation.statusLabel,
                presentation.shareableUrl
            ];
        })
    ]
        .map((row) => row.map(escapeCsvCell).join(","))
        .join("\n");

export const isWhatsappPreRegistrationConflict = (error: any) =>
    String(
        error?.response?.data?.message || error?.message || error || ""
    ).includes("configuration changed while saving");
